import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  App,
  View,
  Table,
  ModalProps,
  NotificationProps,
  AppSpecifier,
} from "@data-root/types";
import { MenuItem, Navigator } from "../../components/navigation";
import { ErrorView } from "@data-root/error-boundary";
import { core, coreConfig } from "../..";
import i18n from "../../i18n";
import { localizeApp } from "../../i18n/localize-app";
import {
  ConfigProvider,
  App as AntdApp,
  ThemeConfig,
  Button,
  theme as antdThemeAlgo,
} from "antd";
import { ThemeContext, isEmbedded } from "../../components/theme-provider";
import {
  ArrowLeftOutlined,
  SyncOutlined,
  LogoutOutlined,
  BookOutlined,
} from "@ant-design/icons";

import { HeaderCenter } from "../../components/HeaderCenter";
import { HeaderRight } from "../../components/HeaderRight";
import { DndContext, DragEndEvent, DragOverlay } from "@dnd-kit/core";
import {
  AppContext,
  DescriptionProvider,
  EXPERIMENT_DASHBOARD_TOKEN,
  IAppContext,
  IDescriptionContext,
  INavigationContext,
  NavigationContext,
  useAsyncCallback,
  useClientContext,
} from "@data-root/react";
import {
  LoaderFunctionArgs,
  Outlet,
  ShouldRevalidateFunctionArgs,
  createSearchParams,
  redirect,
  useLoaderData,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { changeThemeColor } from "../../utils";
import { VERSION } from "../../config/version";
import { assertEigenVersionCompat } from "../../utils/eigen-version-compat";
import esES from "antd/locale/es_ES";
import * as Sentry from "@sentry/react";

interface AppRendererProps {
  app: App;
  base?: string;
}

interface HistoryEntry {
  viewId: string;
  params: Record<string, any>;
}

interface PushAction {
  type: "Push";
  entry: HistoryEntry;
}

interface PopAction {
  type: "Pop";
}

interface SetAction {
  type: "Update";
  params: Record<string, any>;
}

export const checkPermissions = async (app: App): Promise<boolean> => {
  if (!app.configuration?.permissions?.length) return true;

  const queries = await Promise.all(
    app.configuration.permissions.map((permission) =>
      navigator.permissions.query({
        name: permission,
      })
    )
  );

  return queries.every(({ state }) => state === "granted");
};

export const appScreenLoader = async (args: LoaderFunctionArgs) => {
  const { appId, deploymentId, viewId } = args.params;

  const url = new URL(args.request.url);
  const token = url.searchParams.get("token");

  if (token) {
    await core.fromToken(token);
  } else {
    await core
      .recover(coreConfig)
      .catch((e) => {
        console.error(e);
        throw redirect("/auth");
      });
  }

  if (!core.user) {
    const newSearch = new URLSearchParams(url.searchParams);
    newSearch.set("next", url.pathname);

    const redirectUrl = `/auth?${newSearch.toString()}`;
    throw redirect(redirectUrl);
  }

  Sentry.setUser({
    id: core.user!.id,
    username: core.user!.name,
    email: core.user!.email,
  });

  const appSpecifier: AppSpecifier = {
    appId,
    deploymentId,
  };

  await core.loadApp(appSpecifier);
  const { app: rawApp, manifest } = await core.getApp(appSpecifier);

  // Multi-language EigenApps: resolve every localized App-DATA label ({ $i18n: { en, es, ... } })
  // to the active runtime locale ONCE here, so the whole downstream renderer keeps reading plain
  // strings. An all-plain-string App passes through unchanged (zero regression). The active locale
  // is the i18n language (driven by the embed's ?lang= -> advisor language, or the user's switcher).
  const app = localizeApp(rawApp, i18n.language) as App;

  if (manifest.prefersExperimental)
    throw redirect(`/_/app/${appId}/${deploymentId}`);

  // Behavior-identical refactor (A2): the inline semver gate is now the shared
  // assertEigenVersionCompat helper (same throw + same message), so the v2 loader
  // can reuse the exact same gate. See utils/eigen-version-compat.ts.
  assertEigenVersionCompat(app.dependencies, VERSION);

  if (!viewId && app.defaultViewId) {
    const defaultViewId = await core.evaluateFormula<string>(
      app.defaultViewId,
      {
        appId,
        deploymentId,
      }
    );

    if (defaultViewId)
      throw redirect(`/app/${appId}/${deploymentId}/${defaultViewId}`);
  }

  const { primaryColor } = app.styles;
  changeThemeColor(primaryColor);

  return { app };
};

export const appScreenShouldRevalidate = (
  args: ShouldRevalidateFunctionArgs
) => {
  const { currentParams, nextParams } = args;

  const { deploymentId: currentDeploymentId } = currentParams;
  const { deploymentId: nextDeploymentId } = nextParams;

  const { appId: currentAppId } = currentParams;
  const { appId: nextAppId } = nextParams;

  return currentDeploymentId !== nextDeploymentId || currentAppId !== nextAppId;
};

export const AppScreen: FC<{ base?: string }> = (props) => {
  const { app } = useLoaderData() as Awaited<
    ReturnType<typeof appScreenLoader>
  >;

  return <AppRenderer {...props} app={app} />;
};

export const AppRenderer: FC<AppRendererProps> = ({ app, base = "/app" }) => {
  const navigate = useNavigate();
  const { appId, deploymentId, viewId } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();

  const appSpecifier = useMemo<AppSpecifier>(
    () => ({ appId, deploymentId }),
    [appId, deploymentId]
  );

  console.log("Rendering app", appSpecifier);

  const { tables, views, actions, styles, configuration } = app;
  const antdApp = AntdApp.useApp();

  // M2: when this SPA is EMBEDDED (the EigenAdvisor /team live preview), the whole shell must match
  // the advisor's effective theme - not the app's authored palette. A dark-authored demo app would
  // otherwise render a dark-navy layout + light text UNDER the light embed (cards light, shell dark =
  // the reported mismatch). We read the effective theme from the ThemeProvider and drive the antd
  // algorithm + layout/text tokens from it. STANDALONE apps are untouched (zero regression): they keep
  // honoring app.styles exactly as before.
  const { theme: themeMode } = useContext(ThemeContext);
  const embedded = isEmbedded();
  const effectiveDark =
    themeMode === "dark" ||
    (themeMode === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  const clientContext = useClientContext();
  const { core } = clientContext;

  const [pendingSyncN, setPendingSyncN] = useState(0);

  const view = useMemo<View | undefined>(
    () => views.find(({ id }) => id === viewId),
    [viewId, views]
  );
  const tableId = useMemo<string | undefined>(
    () => view && view.tableId,
    [view]
  );

  // const table = useMemo<Table | undefined>(
  //   () => tables.find(({ id }) => id === tableId),
  //   [tableId, tables]
  // );

  const [presence, setPresence] = useState<string[]>([]);

  // const [querySelectedFieldId, setQuerySelectedFieldId] = useState<string>();
  // const queryFieldIdSelectOptions = useMemo<{ value: string }[]>(() => {
  //   if (!table) return [];
  //   const options = [{ value: table.label }];
  //   if (table.secondaryLabel) options.push({ value: table.secondaryLabel });
  //   return options;
  // }, [table]);

  const antdTheme = useMemo<ThemeConfig>(() => {
    // Standalone (not embedded): UNCHANGED - honor the app's authored palette exactly as before.
    if (!embedded) {
      return {
        token: {
          colorPrimary: styles.primaryColor,
          colorText: styles.darkTintColor,
          colorBgLayout: styles.backgroundColor,
          colorLink: styles.lightTintColor,
        },
        components: { Layout: { headerBg: "#ffffff", siderBg: "#ffffff" } },
      };
    }
    // Embedded: follow the advisor's effective theme so the ENTIRE shell (layout bg, text, header,
    // sider, and every antd component via the algorithm) matches - not just the table card.
    if (effectiveDark) {
      return {
        algorithm: antdThemeAlgo.darkAlgorithm,
        token: {
          colorPrimary: styles.primaryColor,
          colorLink: styles.lightTintColor,
          colorText: styles.darkTintColor,
          colorBgLayout: styles.backgroundColor,
        },
        components: {
          Layout: { headerBg: styles.backgroundColor, siderBg: styles.backgroundColor },
        },
      };
    }
    // Light embed (the default + the reported case): let the light algorithm drive a light layout +
    // readable dark text; keep only the app's brand primary/link. Do NOT inject the app's authored
    // (often dark) backgroundColor/darkTintColor here, or the shell stays dark under a light preview.
    return {
      algorithm: antdThemeAlgo.defaultAlgorithm,
      token: {
        colorPrimary: styles.primaryColor,
        colorLink: styles.lightTintColor,
      },
      components: { Layout: { headerBg: "#ffffff", siderBg: "#ffffff" } },
    };
  }, [styles, embedded, effectiveDark]);

  const getView = useCallback(
    (viewId: string): View => {
      const view = views.find(({ id }) => id === viewId);
      if (!view) throw new Error(`[GET_VIEW] View ${viewId} not found.`);
      return view;
    },
    [views]
  );

  const getTable = useCallback(
    (tableId: string): Table => {
      const table = tables.find(({ id }) => id === tableId);
      if (!table) throw new Error(`[GET_TABLE] Table ${tableId} not found.`);
      return table;
    },
    [tables]
  );

  const getAction = useCallback(
    (actionId: string) => {
      const action = actions.find(({ id }) => id === actionId);
      if (!action)
        throw new Error(`[GET_ACTION] Action ${actionId} not found.`);
      return action;
    },
    [actions]
  );

  const sync = useAsyncCallback(async (): Promise<void> => {
    // eslint-disable-next-line prefer-const
    let prevent = false;
    core.emitter.emit("should_sync", core, () => (prevent = true));
    if (prevent) return;

    try {
      await core.sync(appSpecifier);
    } catch (error) {
      // throw throwAsync(error as Error);
      throw error;
    }
    setPendingSyncN(0);
  }, [appSpecifier, core]);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (
        over &&
        over.data!.current!.accepts.includes(active.data!.current!.type)
      ) {
        core.emitter.emit("drop", {
          id: over.id.toString(),
          data: structuredClone(active.data!.current),
        });
      }
    },
    [core.emitter]
  );

  const onPendingSync = useCallback(() => {
    setPendingSyncN((n) => n + 1);
  }, []);

  const onColdStart = useCallback(
    (core, key: string) => {
      antdApp.notification.info({
        key,
        message:
          "Esto puede tardar unos segundos... Te avisaremos cuando terminemos.",
        icon: <SyncOutlined spin />,
      });
    },
    [antdApp.notification]
  );

  const onColdStartDone = useCallback(
    (core, key?: string) => {
      if (key) antdApp.notification.destroy(key);

      antdApp.notification.success({
        message: "Done!",
      });
    },
    [antdApp.notification]
  );

  const onNotification = useCallback(
    (props: NotificationProps) => {
      if (typeof props === "string")
        return antdApp.notification.info({ message: props });

      antdApp.notification.info({
        message: props.content,
      });
    },
    [antdApp.notification]
  );

  const onModal = useCallback(
    (props: ModalProps) => {
      antdApp.modal.confirm({
        centered: true,
        title: "Confirmar",
        content: props.content,
        onOk: props.onOk,
        onCancel: props.onCancel,
        okText: "Aceptar",
        cancelText: "Cancelar",
      });
    },
    [antdApp.modal]
  );

  const onPresence = useCallback((presence: string[]) => {
    setPresence(presence);
  }, []);

  const onUrl = useCallback((url: string) => {
    window.open(url, "_blank");
  }, []);

  const context = useMemo<IAppContext>(
    () => ({
      app,
      configuration,

      pendingSyncN,

      appSpecifier,
      appId,
      deploymentId,
      tableId,
      viewId,

      menu: app.menu,
      viewGroups: app.viewGroups,

      tables,
      views,
      actions,
      styles,

      getView,
      getTable,
      getAction,

      sync,
    }),
    [
      app,
      configuration,
      pendingSyncN,
      appSpecifier,
      appId,
      deploymentId,
      tableId,
      viewId,
      tables,
      views,
      actions,
      styles,
      getView,
      getTable,
      getAction,
      sync,
    ]
  );

  const historyReducer = useCallback(
    (history: HistoryEntry[], action: PushAction | PopAction | SetAction) => {
      switch (action.type) {
        case "Push":
          return [...history, action.entry];
        case "Pop":
          return history.slice(0, -1);
        case "Update":
          return [
            ...history.slice(0, -1),
            {
              ...history[history.length - 1],
              params: {
                ...history[history.length - 1].params,
                ...action.params,
              },
            },
          ];
      }
    },
    []
  );

  const [history, dispatchToHistory] = useReducer(historyReducer, [
    {
      viewId,
      params: Object.fromEntries(searchParams.entries()),
    },
  ]);

  const navigation = useMemo<INavigationContext>(() => {
    console.log("navigation");

    return {
      navigation: {
        navigate(viewId, params, { nested = false } = {}) {
          if (viewId === EXPERIMENT_DASHBOARD_TOKEN) {
            navigate({
              pathname: `${base}/${appId}/${deploymentId}/_/dashboard`,
              search: createSearchParams(params as any).toString(),
            });

            dispatchToHistory({
              type: "Push",
              entry: {
                viewId: EXPERIMENT_DASHBOARD_TOKEN,
                params: {
                  ...params,
                },
              },
            });

            return;
          }

          if (!!nested)
            navigate({
              pathname: `${base}/${appId}/${deploymentId}/${viewId}/${nested}`,
              search: createSearchParams(params as any).toString(),
            });
          else
            navigate({
              pathname: `${base}/${appId}/${deploymentId}/${viewId}`,
              search: createSearchParams(params as any).toString(),
            });

          dispatchToHistory({
            type: "Push",
            entry: {
              viewId: viewId as string,
              params: {
                ...params,
                viewId,
              },
            },
          });
        },
        canGoBack() {
          return history.length > 1;
        },
        goBack() {
          if (!(history.length > 1)) return;

          const previousEntry = history[history.length - 2];
          dispatchToHistory({ type: "Pop" });

          if (previousEntry.viewId === EXPERIMENT_DASHBOARD_TOKEN)
            navigate({
              pathname: `${base}/${appId}/${deploymentId}/_/dashboard`,
              search: createSearchParams(previousEntry.params).toString(),
            });
          else if (previousEntry.viewId)
            navigate({
              pathname: `${base}/${appId}/${deploymentId}/${previousEntry.viewId}`,
              search: createSearchParams(previousEntry.params).toString(),
            });
        },
        setParams(params) {
          for (const key in params) searchParams.set(key, params[key]);
          setSearchParams(searchParams, { replace: true });

          dispatchToHistory({
            type: "Update",
            params,
          });
        },
        getParam(param, defaultValue) {
          return searchParams.get(param) || defaultValue;
        },
        getViewId() {
          return viewId;
        },
        getParent(id) {
          return this;
        },
      },
    };
  }, [
    appId,
    base,
    deploymentId,
    history,
    navigate,
    searchParams,
    setSearchParams,
    viewId,
  ]);

  const menu = useMemo<MenuItem[]>(
    () => [
      {
        key: "-1",
        label: "Borradores",
        icon: <BookOutlined />,
        onClick: () => {
          navigate({
            pathname: `${base}/${appId}/${deploymentId}/_/drafts`,
          });
          // setCounter((c) => c + 1);
        },
      },
      {
        key: "0",
        label: "Ir a biblioteca",
        icon: <ArrowLeftOutlined />,
        onClick: () => navigate("/library"),
      },
      {
        key: "1",
        label: "Cerrar sesión",
        icon: <LogoutOutlined />,
        onClick: clientContext.logout,
      },
    ],
    [appId, base, clientContext.logout, deploymentId, navigate]
  );

  const onQuery = useAsyncCallback(
    async (query: string) => {
      // const queryFieldId = querySelectedFieldId || table.label;
      // const params = {
      //   queryFieldId,
      //   query,
      // };
      // navigationRef.setParams(params as never);
      // core.emitter.emit("search", params);

      navigation.navigation.setParams({ query });
    },
    [navigation.navigation]
  );

  const onView = useCallback(
    (
      viewId: string,
      params?: {
        tableId: string;
        rowId: string;
        deltaId: string;
      },
      options?: {
        nested?: string;
      }
    ) => {
      navigation.navigation.navigate(viewId, params, options);
    },
    [navigation.navigation]
  );

  useEffect(() => {
    core.emitter.on("coldstart", onColdStart);
    core.emitter.on("coldstart:done", onColdStartDone);
    core.emitter.on("pending_sync", onPendingSync);
    core.emitter.on("notification", onNotification);
    core.emitter.on("modal", onModal);
    core.emitter.on("presence", onPresence);
    core.emitter.on("url", onUrl);
    core.emitter.on("view", onView);

    return () => {
      core.emitter.off("coldstart", onColdStart);
      core.emitter.off("coldstart:done", onColdStartDone);
      core.emitter.off("pending_sync", onPendingSync);
      core.emitter.off("notification", onNotification);
      core.emitter.off("modal", onModal);
      core.emitter.off("presence", onPresence);
      core.emitter.off("url", onUrl);
      core.emitter.off("view", onView);
    };
  }, [
    core,
    antdApp,
    onColdStart,
    onColdStartDone,
    onPendingSync,
    onNotification,
    onPresence,
    onModal,
    onUrl,
    onView,
  ]);

  const descriptionRef = useRef<IDescriptionContext>(null);

  return (
    <Sentry.ErrorBoundary fallback={ErrorView as any}>
      <ConfigProvider theme={antdTheme} locale={esES}>
        <DndContext onDragEnd={onDragEnd}>
          <AppContext.Provider value={context}>
            <DescriptionProvider ref={descriptionRef}>
              <NavigationContext.Provider value={navigation}>
                <Navigator
                  views={views}
                  headerCenterItems={[
                    <HeaderCenter
                      key="center"
                      view={view}
                      styles={styles}
                      onQuery={onQuery}
                    />,
                  ]}
                  headerRightItems={[
                    <HeaderRight
                      key="right"
                      pendingSyncN={pendingSyncN}
                      sync={sync}
                      presence={presence}
                    />,
                  ]}
                  menu={menu}
                >
                  <Outlet />
                </Navigator>
              </NavigationContext.Provider>
            </DescriptionProvider>
          </AppContext.Provider>

          <DragOverlay>
            <Button type="primary">Dragging</Button>
          </DragOverlay>
        </DndContext>
      </ConfigProvider>
    </Sentry.ErrorBoundary>
  );
};
